import { ComponentProps } from "../registry";
import { useBound, writePath } from "../helpers";
import { BoundList, BoundValue } from "../../types";

interface Option {
  label: string;
  value: unknown;
}

/**
 * The catalog declares `options` as an unconstrained BoundList, so both
 * shapes are legal and must render:
 *   - plain scalars ("approve")          → the scalar is label AND value
 *   - structured ({label, value} pairs)  → value falls back to label and
 *                                          vice versa when one is missing
 */
function normalizeOption(raw: unknown, index: number): Option {
  if (raw !== null && typeof raw === "object") {
    const o = raw as { label?: unknown; value?: unknown };
    const value = o.value !== undefined ? o.value : o.label;
    const label =
      o.label !== undefined ? String(o.label)
      : value !== undefined ? String(value)
      : String(index);
    return { label, value };
  }
  return { label: String(raw), value: raw };
}

export function MultipleChoice({ id, fields, scope }: ComponentProps) {
  const rawOptions = useBound<unknown[]>(fields.options as BoundValue, scope) ?? [];
  const options = rawOptions.map(normalizeOption);
  const selections = useBound<unknown[]>(fields.selections as BoundValue, scope) ?? [];
  const variant = useBound<string>(fields.variant as BoundValue, scope) ?? "checkbox";
  const max = useBound<number>(fields.maxAllowedSelections as BoundValue, scope);
  const path = (fields.selections as BoundList | undefined)?.path;

  const selSet = new Set(selections);
  const toggle = (val: unknown) => {
    if (!path) return;
    let next: unknown[];
    if (selSet.has(val)) {
      next = selections.filter((v) => v !== val);
    } else {
      next = [...selections, val];
      if (max && next.length > max) next = next.slice(-max);
    }
    writePath(path, next);
  };

  if (variant === "select") {
    return (
      <select
        className="a2-field-input"
        multiple={!max || max > 1}
        value={selections.map(String)}
        onChange={(e) => {
          if (!path) return;
          const vals = Array.from(e.target.selectedOptions).map(o => o.value);
          writePath(path, vals);
        }}
      >
        {options.map((o, i) => (
          <option key={i} value={String(o.value)}>{o.label}</option>
        ))}
      </select>
    );
  }
  if (variant === "radio") {
    return (
      <div className="a2-multichoice radio">
        {options.map((o, i) => (
          <label key={i} className="a2-multichoice-item">
            <input
              type="radio"
              name={path ?? id}
              checked={selSet.has(o.value)}
              onChange={() => path && writePath(path, [o.value])}
            />
            {o.label}
          </label>
        ))}
      </div>
    );
  }
  // chips and checkbox both render the same way for v1 — checkbox-style toggles
  return (
    <div className={`a2-multichoice ${variant}`}>
      {options.map((o, i) => (
        <label key={i} className={`a2-multichoice-item${selSet.has(o.value) ? " selected" : ""}`}>
          {variant === "chips" ? (
            <button type="button" onClick={() => toggle(o.value)}>{o.label}</button>
          ) : (
            <>
              <input type="checkbox" checked={selSet.has(o.value)} onChange={() => toggle(o.value)} />
              {o.label}
            </>
          )}
        </label>
      ))}
    </div>
  );
}
